// Vocabulario de pestañas limitables por el owner de una Productora/Estudio.
//
// CRITERIO DE LA `key` (importante para que el filtrado del sidebar case):
// El sidebar identifica cada item navegable de dos maneras distintas:
//   1) Las ETAPAS (STAGE_CONFIG) se recorren por su `stage.name` (StageName).
//   2) El resto de items (PROYECTO + DOCUMENTOS + "Plan Festivales") son NavItems
//      y se identifican por su `path`.
// Para tener UNA clave estable por pestaña reutilizamos exactamente ese identificador:
//   - pestaña de etapa  -> key = StageName (p.ej. 'narrativa', 'presupuesto').
//   - pestaña NavItem    -> key = path (p.ej. '/planos', '/produccion/equipo').
// Así el filtro del sidebar puede preguntar `allowed.has(stage.name)` para las etapas
// y `allowed.has(item.path)` para los NavItems sin traducciones intermedias.
//
// NO se incluyen las pestañas de la sección CUENTA (Perfil, Mi voz, Suscripción):
// son datos personales/de facturación y no se limitan por miembro.
//
// Excluimos de STAGE_CONFIG las etapas que el sidebar tampoco pinta como etapa
// (STAGES_FUERA_DEL_PIPELINE: 'festivales', 'postproduccion' y 'eco'): cada una tiene su
// propio NavItem ("Plan Festivales", "Breakdown VFX" y "Huella de Carbono") y saldría
// duplicada en esta lista, con dos claves distintas para la misma pestaña.

import { STAGE_CONFIG, esStageDePipeline } from '@/types/scriptAnalysis';
import type { StageName } from '@/types/scriptAnalysis';

export interface TeamTab {
  /** Clave estable: StageName para etapas, path para el resto de items del sidebar. */
  key: string;
  /** Etiqueta visible (español de España). */
  label: string;
}

// Etapas visibles en el sidebar (mismas exclusiones que Sidebar.tsx).
const STAGE_TABS: TeamTab[] = STAGE_CONFIG
  .filter((s) => esStageDePipeline(s.name))
  .map((s) => ({ key: s.name as StageName, label: s.label }));

// Item que cuelga de Audiencias en la nav (no es una etapa del pipeline).
const POST_STAGE_TAB: TeamTab = { key: '/distribucion/plan-festivales', label: 'Plan Festivales' };

// Huella de Carbono: su chat es la etapa 'eco' (el Eco Manager), pero esa etapa se queda
// fuera del pipeline de la nav y el sidebar la cuelga como item propio justo detrás de
// Presupuesto. Se inserta aquí en esa misma posición para que la lista coincida con lo que
// ve el usuario. OJO: la clave es el path '/huella-carbono', NO el StageName 'eco'. Hay
// organizaciones con ese path guardado en allowed_tabs; cambiarlo las dejaría sin la
// pestaña, y además es el path lo que compara el sidebar para este item.
const CARBONO_TAB: TeamTab = { key: '/huella-carbono', label: 'Huella de Carbono' };
const presupuestoIdx = STAGE_TABS.findIndex((t) => t.key === 'presupuesto');
const STAGE_TABS_CON_CARBONO: TeamTab[] = [
  ...STAGE_TABS.slice(0, presupuestoIdx + 1),
  CARBONO_TAB,
  ...STAGE_TABS.slice(presupuestoIdx + 1),
];

// Items de PROYECTO (keyed por path, igual que en el sidebar).
const PROYECTO_TABS: TeamTab[] = [
  { key: '/planos', label: 'Planos' },
  { key: '/produccion/escenas', label: 'Escenas' },
  { key: '/produccion/mapa', label: 'Mapa' },
  { key: '/produccion/vfx', label: 'Breakdown VFX' },
];

// Items de DOCUMENTOS (keyed por path).
const DOCUMENTOS_TABS: TeamTab[] = [
  { key: '/proyecto/documentos', label: 'Memorias de intenciones' },
  { key: '/produccion/equipo', label: 'Equipo y Reparto' },
  { key: '/dossier-builder', label: 'Dossier Builder' },
];

// Lista canónica de pestañas limitables, en el orden en que aparecen en el sidebar.
export const TEAM_TABS: TeamTab[] = [
  ...STAGE_TABS_CON_CARBONO,
  POST_STAGE_TAB,
  ...PROYECTO_TABS,
  ...DOCUMENTOS_TABS,
];

// Todas las claves (útil para "marcar todas" / comparar con allowed_tabs).
export const ALL_TAB_KEYS: string[] = TEAM_TABS.map((t) => t.key);

/**
 * Un miembro ve una pestaña si es owner, si `allowed` es null (todas) o si la clave
 * de la pestaña está en `allowed`. El owner nunca se filtra por aquí.
 */
export function tabAllowed(allowed: string[] | null | undefined, key: string): boolean {
  if (allowed == null) return true;
  return allowed.includes(key);
}
